import type { FlowGraph, NodeType } from '@/features/flows/types.ts'

/**
 * Where a new node's `id` and `key` come from.
 *
 * Both are **minted here, on the client, and kept** (D9, ADR-0007 §6): the
 * engine never renumbers a node on save, because a run step cites the id it saw
 * and a graph the server had re-identified would orphan every step of every run
 * that had already happened.
 *
 * The two are minted by different rules on purpose. An `id` only has to be
 * unique and stable, so it is random. A `key` is what a template writes
 * (`{{ made.leadId }}`) and what a run step falls back to when the node is
 * gone, so it is derived from the node's type and readable — and it has three
 * ways to be refused (`invalid_key`, `duplicate_key`, `retired_key` in
 * `packages/core/src/flows/validate.ts`), all of which are avoided here rather
 * than discovered at save.
 */

/** Mirrors `KEY_PATTERN` in `packages/core/src/flows/validate.ts`. */
const KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_-]*$/

/**
 * Forty bits of randomness as ten hex characters.
 *
 * `crypto.getRandomValues` rather than `crypto.randomUUID`, because
 * `randomUUID` is only defined in a **secure context** and the console is meant
 * to be opened over plain http on a LAN — `main.tsx`'s dev block exists so "a
 * device without DevTools (a phone over LAN / a tunnel)" can seed its token.
 * On such an origin `randomUUID` is undefined, so every Add and Duplicate threw
 * inside the store's mutator and the data router's error boundary replaced the
 * whole app. `getRandomValues` is available on insecure origins.
 */
function randomSuffix(): string {
  const bytes = new Uint8Array(5)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * A random id in the engine's own spelling (`newFlowGraph` mints `n_trigger`).
 *
 * It is checked against the graph rather than trusted: 40 bits of randomness
 * would collide about once in a million graphs of a thousand nodes, and a
 * duplicate id is a `duplicate_node_id` refusal at save — cheap to make
 * impossible, expensive to explain.
 */
export function mintNodeId(graph: FlowGraph): string {
  const taken = new Set(graph.nodes.map((n) => n.id))
  for (;;) {
    const id = `n_${randomSuffix()}`
    if (!taken.has(id)) return id
  }
}

/**
 * A readable key for a node of `type`, free of every key the graph holds and of
 * every key it has retired.
 *
 * The stem is the type with its dotted prefix dropped — `trigger.status_entered`
 * becomes `status_entered` — because `.` is a path separator in the data chain
 * and a key carrying one could not be written in a template.
 */
export function mintNodeKey(graph: FlowGraph, type: NodeType): string {
  const stem = type.slice(type.lastIndexOf('.') + 1)
  const base = KEY_PATTERN.test(stem) ? stem : 'node'
  const taken = new Set<string>([...graph.nodes.map((n) => n.key), ...(graph.retiredKeys ?? [])])
  if (!taken.has(base)) return base
  for (let n = 2; ; n += 1) {
    const candidate = `${base}_${n}`
    if (!taken.has(candidate)) return candidate
  }
}
